/**
 * Magasins `catalog_items` et `catalog_meta` : le catalogue en place sur le
 * poste et ce qui le decrit.
 *
 * catalog_meta contient un seul enregistrement (cle CATALOG_META_KEY) :
 *   { key, snapshot_id, kid, format, store_id, item_count,
 *     weight_barcode_prefix, loaded_at, confirmed_at }
 * confirmed_at = derniere confirmation par le serveur (200 verifie ou 304) :
 * c'est de la que se mesure l'age du catalogue (app/catalogFreshness.js).
 *
 * Remplacement : articles ET description dans UNE transaction. Une erreur au
 * milieu annule tout : l'ancien catalogue reste entier.
 */
import { requestToPromise, transactionDone } from './idb';
import { CATALOG_META_KEY, STORES } from './schema';

/** Description du catalogue en place, ou null si le poste n'en a pas. */
export async function getCatalogMeta(db) {
    const tx = db.transaction(STORES.CATALOG_META, 'readonly');
    const record = await requestToPromise(tx.objectStore(STORES.CATALOG_META).get(CATALOG_META_KEY));
    return record || null;
}

/** Nombre d'articles en place. */
export async function countCatalogItems(db) {
    const tx = db.transaction(STORES.CATALOG_ITEMS, 'readonly');
    return requestToPromise(tx.objectStore(STORES.CATALOG_ITEMS).count());
}

/**
 * Article du catalogue tel qu'il est range : l'article du serveur, plus les
 * champs que lisent les index du schema v1 (`codes` multi-entrees et
 * `weight_sku`). Scan par sku seulement (decision du 2026-10-18) : `codes`
 * ne contient que le sku.
 */
export function toStoredItem(item) {
    const stored = { ...item, codes: typeof item.sku === 'string' && item.sku !== '' ? [item.sku] : [] };
    if (item.weight && typeof item.weight.sku === 'string' && item.weight.sku !== '') {
        stored.weight_sku = item.weight.sku;
    }
    return stored;
}

/**
 * Remplace tout le catalogue, atomiquement.
 * @param {IDBDatabase} db
 * @param {{ items: object[], meta: object }} catalog meta sans `key`
 */
export async function replaceCatalog(db, { items, meta }) {
    const tx = db.transaction([STORES.CATALOG_ITEMS, STORES.CATALOG_META], 'readwrite');
    const done = transactionDone(tx);
    try {
        const itemStore = tx.objectStore(STORES.CATALOG_ITEMS);
        itemStore.clear();
        for (const item of items) itemStore.put(toStoredItem(item));
        tx.objectStore(STORES.CATALOG_META).put({ ...meta, key: CATALOG_META_KEY });
    } catch (error) {
        // Un put() qui leve (valeur non clonable, cle absente) : on annule tout.
        done.catch(() => {});
        tx.abort();
        throw error;
    }
    await done;
}

/**
 * Le serveur confirme que le catalogue en place est a jour (304) : seule la
 * date de confirmation change, et seulement si c'est bien le meme instantane.
 * @returns {Promise<boolean>} faux si le catalogue a change entre-temps
 */
export async function markCatalogConfirmed(db, { snapshotId, kid, at }) {
    const tx = db.transaction(STORES.CATALOG_META, 'readwrite');
    const store = tx.objectStore(STORES.CATALOG_META);
    const done = transactionDone(tx);
    const current = await requestToPromise(store.get(CATALOG_META_KEY));
    let updated = false;
    if (current && current.snapshot_id === snapshotId && current.kid === kid) {
        store.put({ ...current, confirmed_at: at });
        updated = true;
    }
    await done;
    return updated;
}
